import React, { useState, useEffect, useCallback, useContext } from "react";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import Shell from "./Shell";
import { useAdminStyles, brl, num } from "./styles";

// Rótulos dos medidores. O nome técnico (`ai.tokens.out`) serve para o log, não
// para uma tela que alguém lê todo dia.
const METER_LABEL = {
  "call.inbound": "Chamadas recebidas",
  "call.outbound": "Chamadas originadas",
  "whatsapp.message": "WhatsApp",
  "sms.message": "SMS",
  "ai.call": "Chamadas de IA",
  "ai.tokens.in": "Tokens de IA (entrada)",
  "ai.tokens.out": "Tokens de IA (saída)",
  "agent.voice": "Agente de voz",
  "stt.recognition": "Transcrição",
  "tts.render": "Síntese de voz",
  "code.run": "Execução de código",
  "flow.execution": "Execuções de fluxo",
  "storage.gb": "Armazenamento",
};

const Kpi = ({ label, value, hint }) => {
  const c = useAdminStyles();
  return (
    <div className={c.kpi}>
      <div className={c.kpiLabel}>{label}</div>
      <div className={c.kpiValue}>{value}</div>
      {hint ? <div className={c.kpiHint}>{hint}</div> : null}
    </div>
  );
};

const Overview = () => {
  const classes = useAdminStyles();
  // o servidor já remove os campos de dinheiro para o suporte; aqui é só para
  // não desenhar cartão vazio
  const { user } = useContext(AuthContext);
  const podeVerDinheiro = !!(user && user.canSeeMoney);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/overview");
      setData(d);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <Shell title="Backoffice">
        <div className={classes.empty}>Carregando…</div>
      </Shell>
    );
  }

  const { users, funnel, plans, billing, usage, assets, mrrCents } = data;
  // fallback p/ payload antigo em cache (antes dos cards de minutos/chamadas)
  const minutes = data.minutes || { inbound: 0, outbound: 0, total: 0, maxCallMin: 0 };
  const activeCalls = data.activeCalls || { total: 0, owners: 0, maxPerOwner: 0 };
  const topFunnel = funnel.length ? funnel[0].count || 1 : 1;
  const maxMeter = usage.meters.reduce((m, x) => Math.max(m, x.amountCents), 0) || 1;

  return (
    <Shell title="Backoffice">
      <div className={classes.cards}>
        <Kpi label="Usuários" value={num(users.total)} hint={`+${users.new30} em 30 dias`} />
        <Kpi label="Ativos (30d)" value={num(users.active30)} hint="executaram algum fluxo" />
        {podeVerDinheiro && (
          <Kpi label="MRR" value={brl(mrrCents)} hint={`${plans.reduce((a, p) => a + (p.mrrCents ? p.users : 0), 0)} assinaturas pagas`} />
        )}
        <Kpi label="Execuções (30d)" value={num(usage.runs30)} hint={`${usage.runs24} nas últimas 24h`} />
        <Kpi label="Erro nas execuções" value={`${usage.errorRate30}%`} hint="dos runs em 30 dias" />
        <Kpi
          label="Chamadas em curso"
          value={num(activeCalls.total)}
          hint={`${num(activeCalls.owners)} conta(s) agora${
            activeCalls.maxPerOwner > 5 ? ` · ⚠ 1 conta com ${num(activeCalls.maxPerOwner)}` : ""
          }`}
        />
        <Kpi
          label="Minutos trafegados (30d)"
          value={`${num(Math.round(minutes.total))} min`}
          hint={`${num(Math.round(minutes.inbound))} recebidas · ${num(Math.round(minutes.outbound))} feitas${
            minutes.maxCallMin > 60 ? ` · ⚠ maior chamada: ${num(Math.round(minutes.maxCallMin))} min` : ""
          }`}
        />
        {podeVerDinheiro && (
          <Kpi label="Consumo (30d)" value={brl(usage.meters.reduce((a, m) => a + m.amountCents, 0))} hint="tarifado por medidor" />
        )}
      </div>

      <div className={classes.cols}>
        <div className={classes.card}>
          <div className={classes.cardTitle}>Onde os cadastros param</div>
          <div className={classes.cardSub}>
            Cada etapa conta quantos clientes chegaram até ela — a maior queda é o gargalo do produto.
          </div>
          {funnel.map((f, i) => (
            <div key={f.label} className={`${classes.row} ${i === 0 ? classes.rowFirst : ""}`}>
              <div className={classes.grow}>
                <div className={classes.strong}>{f.label}</div>
                <div className={classes.bar}>
                  <div className={classes.barFill} style={{ width: `${Math.round((f.count / topFunnel) * 100)}%` }} />
                </div>
              </div>
              <div className={classes.num}>{num(f.count)}</div>
            </div>
          ))}
        </div>

        {podeVerDinheiro && (
        <div className={classes.card}>
          <div className={classes.cardTitle}>Planos</div>
          <div className={classes.cardSub}>Usuários no plano e receita recorrente que ele gera.</div>
          {plans.map((p, i) => (
            <div key={p.code} className={`${classes.row} ${i === 0 ? classes.rowFirst : ""}`}>
              <div className={classes.grow}>
                <div className={classes.strong}>{p.name}</div>
                <div className={classes.muted}>{num(p.users)} usuário(s)</div>
              </div>
              <div className={classes.num}>{brl(p.mrrCents)}</div>
            </div>
          ))}
        </div>
        )}
      </div>

      <div className={classes.cols}>
        {podeVerDinheiro && (
        <div className={classes.card}>
          <div className={classes.cardTitle}>Consumo por medidor (30 dias)</div>
          <div className={classes.cardSub}>Receita de uso — separada da mensalidade.</div>
          {usage.meters.length === 0 && <div className={classes.empty}>Nada consumido no período.</div>}
          {usage.meters.map((m, i) => (
            <div key={m.meter} className={`${classes.row} ${i === 0 ? classes.rowFirst : ""}`}>
              <div className={classes.grow}>
                <div className={classes.strong}>{METER_LABEL[m.meter] || m.meter}</div>
                <div className={classes.bar}>
                  <div className={classes.barFill} style={{ width: `${Math.round((m.amountCents / maxMeter) * 100)}%` }} />
                </div>
              </div>
              <div className={classes.num}>{brl(m.amountCents)}</div>
            </div>
          ))}
        </div>
        )}

        <div>
          {podeVerDinheiro && billing && (
          <div className={classes.card}>
            <div className={classes.cardTitle}>Cobrança</div>
            <div className={classes.cardSub}>O que está em aberto agora e o que entrou em 30 dias.</div>
            <div className={`${classes.row} ${classes.rowFirst}`}>
              <div className={classes.grow}><div className={classes.strong}>Faturas em aberto</div></div>
              <div className={classes.num}>{num(billing.openInvoices)} · {brl(billing.openCents)}</div>
            </div>
            <div className={classes.row}>
              <div className={classes.grow}><div className={classes.strong}>Assinaturas em atraso</div></div>
              <div className={classes.num}>{num(billing.pastDue)}</div>
            </div>
            <div className={classes.row}>
              <div className={classes.grow}><div className={classes.strong}>Recebido (30d)</div></div>
              <div className={classes.num}>{num(billing.paid30)} · {brl(billing.paid30Cents)}</div>
            </div>
            <div className={classes.row}>
              <div className={classes.grow}><div className={classes.strong}>Cobranças recusadas (30d)</div></div>
              <div className={classes.num}>{num(billing.failed30)}</div>
            </div>
          </div>
          )}

          <div className={classes.card}>
            <div className={classes.cardTitle}>O que existe na plataforma</div>
            <div className={classes.cardSub}>Volume construído pelos clientes.</div>
            {[
              ["Fluxos", assets.applications],
              ["Publicados", assets.published],
              ["Agentes", assets.agents],
              ["Ferramentas", assets.tools],
              ["Números", assets.numbers],
              ["Conectores", assets.connectors],
              ["Agendamentos ativos", assets.schedules],
            ].map(([label, v], i) => (
              <div key={label} className={`${classes.row} ${i === 0 ? classes.rowFirst : ""}`}>
                <div className={classes.grow}><div className={classes.strong}>{label}</div></div>
                <div className={classes.num}>{num(v)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Shell>
  );
};

export default Overview;
